import { ActionIcon, Anchor, Button, Menu, Text, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconChevronLeft,
  IconCopy,
  IconDotsVertical,
  IconGitBranch,
  IconGitCompare,
  IconSparkles,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import router from 'next/router';
import { useEffect, useState } from 'react';
import { GitBranchBadge } from '~/components/common/GitBranchBadge';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import { useStartRecorder } from '~/hooks/vm/useStartRecorder';
import { api } from '~/trpc-extra/api';
import EditableTitle from '../../common/EditableTitle';
import CodeViewSwitcher from '../CodeViewSwitcher';
import PublishButton from '../PublishButton';
import { VersionCompareTrunk } from '../VersionCompareTrunk';
import { GitBranchForm } from './GitBranchForm';

export default function TestDraftHeader({
  testDraftId,
  testId,
  testName,
  gitBranch,
  hasTestCode,
  fromTest,
}: {
  testDraftId: string;
  testId: string;
  testName: string;
  gitBranch?: string;
  hasTestCode: boolean;
  fromTest?: boolean;
}) {
  const { isAdmin } = useAuthData();
  const clipboard = useClipboard();
  const trpcUtils = api.useUtils();
  const projectId = useProjectId();
  const { recorderInitiator } = useLiveStateManager();
  const searchParam = useSearchParams();
  const startRecorder = useStartRecorder({ testContentId: testDraftId });
  const [isSourceControlMenuOpen, setIsSourceControlMenuOpen] = useState(false);

  const { mutateAsync: deleteDraft } = api.testDraft.delete.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Deleting...', color: 'gray' });
    },
  });
  const { mutateAsync: duplicateDraft } = api.testDraft.duplicate.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Duplicating draft...', color: 'gray' });
    },
    onSuccess: async (newDraft) => {
      notifications.show({ message: 'Draft duplicated', color: 'blue' });
      window.open(`/project/${projectId}/testDraft/${newDraft.id}`, '_blank', 'noreferrer');
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error duplicating draft', color: 'red' });
    },
  });
  const { mutateAsync: updateTest } = api.test.updateTest.useMutation({
    onSuccess: () => {
      trpcUtils.test.getTest.invalidate().catch(() => undefined);
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Error saving', color: 'red' });
    },
  });

  const { mutateAsync: getTestDefinition } = api.test.getTestDefinition.useMutation();
  const { data: testMeta } = api.test.getMeta.useQuery({ id: testId });

  const { mutate: generateTestName, isPending: isGeneratingTestName } = api.test.generateTestName.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Generating test name...', color: 'gray' });
    },
    onSuccess: () => {
      trpcUtils.test.getTest.invalidate().catch(() => undefined);
      trpcUtils.testContent.get.invalidate().catch(() => undefined);
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
      notifications.show({ message: 'Test name updated', color: 'blue' });
    },
    onError: () => {
      notifications.show({ message: 'Error generating test name', color: 'red' });
    },
  });

  const onCopyTestDefinitionClick = async () => {
    const testDefinition = await getTestDefinition({
      id: testId,
      projectId: projectId || '',
    });
    const testDefinitionJSON = JSON.stringify(testDefinition);
    clipboard.copy(testDefinitionJSON);
    notifications.show({
      title: 'Test definition copied successfully',
      message: 'The test definition was copied to your clipboard',
    });
  };

  useEffect(() => {
    if (testName == 'New Test' && recorderInitiator?.executionType == 'preview' && !isGeneratingTestName) {
      generateTestName({ testContentId: testDraftId });
    }
  }, [recorderInitiator]);

  // Start preview when the url has a preview=true query param
  useEffect(() => {
    if (searchParam?.get('preview') === 'true') {
      startRecorder({ executionType: 'preview' });
    }
  }, [searchParam]);

  return (
    <div className="flex h-[55px] w-full items-center justify-between border-b border-gray-300 bg-stone-900 px-8 py-3 font-medium dark:border-gray-700">
      <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
        {fromTest ?
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => router.back()}
          >
            <IconX size={16} />
          </ActionIcon>
        : <Anchor
            unstyled
            component={Link}
            href={`/project/${projectId}/testDraft`}
            className="text-sm text-gray-200 hover:text-gray-100"
          >
            <IconChevronLeft size={16} />
          </Anchor>
        }
        <Anchor
          unstyled
          component={Link}
          href={`/project/${projectId}/testDraft`}
          className="text-sm text-gray-300 hover:text-gray-200"
        >
          Drafts
        </Anchor>
        <div className="text-gray-300">/</div>
        <EditableTitle
          classNames={{
            text: 'text-gray-300',
            input: 'text-gray-300 bg-transparent',
          }}
          title={testName || 'Untitled Test'}
          tooltip="Click to edit the test name"
          readOnly={isGeneratingTestName || recorderInitiator?.executionType == 'preview'}
          handleSave={async (newName) => {
            await updateTest({ testId, testData: { name: newName } });
          }}
        />

        {testName === 'New Test' && (
          <Button
            variant="light"
            size="compact-xs"
            loading={isGeneratingTestName}
            onClick={() => generateTestName({ testContentId: testDraftId })}
            leftSection={<IconSparkles size={16} />}
          >
            Rename
          </Button>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center space-x-2">
        {gitBranch ?
          <GitBranchBadge
            gitBranch={gitBranch}
            badgeProps={{
              // mantine auto changes styles between dark and light mode, so we hard code the styles here for the test draft header
              classNames: {
                root: 'bg-[var(--mantine-color-dark-6)] border-[var(--mantine-color-dark-4)] text-[var(--mantine-color-gray-4)] normal-case font-semibold',
              },
            }}
          />
        : null}
        {hasTestCode ?
          <>
            {/* TODO: re-enable this when we have time to improve AI suggestions, they are currently not working well */}
            {/* <AISuggestion testContentId={testDraftId} /> */}
            <Menu
              position="bottom-end"
              onOpen={() => setIsSourceControlMenuOpen(true)}
              onClose={() => setIsSourceControlMenuOpen(false)}
            >
              <Menu.Target>
                <Tooltip
                  label="Source control"
                  position="bottom"
                  disabled={isSourceControlMenuOpen}
                >
                  <Button
                    variant="outline"
                    size="compact-sm"
                    color="gray"
                  >
                    <IconGitBranch size={16} />
                  </Button>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconGitCompare size={16} />}
                  disabled={!testMeta?.trunkContentId}
                  onClick={() =>
                    modals.open({
                      title: 'Compare with published version',
                      size: '100%',
                      centered: true,
                      classNames: {
                        body: 'h-[80vh] overflow-auto p-0',
                      },
                      children: (
                        <VersionCompareTrunk
                          testId={testId}
                          testDraftId={testDraftId}
                        />
                      ),
                    })
                  }
                >
                  Compare versions
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconGitBranch size={16} />}
                  onClick={() => {
                    modals.open({
                      title: 'Set Git Branch',
                      centered: true,
                      children: (
                        <GitBranchForm
                          initialGitBranch={gitBranch || ''}
                          testDraftId={testDraftId}
                        />
                      ),
                    });
                  }}
                >
                  Set Git Branch
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <CodeViewSwitcher color="dark" />
            <PublishButton testContentId={testDraftId} />
          </>
        : null}

        <Menu position="bottom-end">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              role="button"
              aria-label="Open test draft menu"
            >
              <IconDotsVertical size={20} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            {!!isAdmin && (
              <Menu.Item
                leftSection={<IconCopy size={16} />}
                color="orange"
                onClick={onCopyTestDefinitionClick}
              >
                Copy Definition
              </Menu.Item>
            )}
            {!!hasTestCode && (
              <Menu.Item
                component="div"
                leftSection={<IconCopy size={16} />}
                onClick={() => duplicateDraft({ testContentId: testDraftId })}
              >
                Duplicate
              </Menu.Item>
            )}
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                modals.openConfirmModal({
                  title: 'Delete Draft',
                  children: (
                    <Text size="sm">Are you sure you want to delete this draft? This action cannot be undone.</Text>
                  ),
                  centered: true,
                  labels: { confirm: 'Delete', cancel: 'Cancel' },
                  confirmProps: { color: 'red' },
                  onConfirm: async () => {
                    await deleteDraft({ ids: [testDraftId] });
                    if (fromTest) {
                      router.back();
                    } else {
                      router.push(`/project/${projectId}/testDraft`).catch(() => undefined);
                    }
                  },
                });
              }}
            >
              Delete Draft
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
}
